/**
 * Estimation Page — router for all estimation tools.
 *
 * Cost Breakdown v2: everything runs on ONE estimate spine (core.estimates —
 * the same registry as the header Estimates dropdown, MTO, and award-to-job).
 * Select an estimate (header dropdown or Estimate Log) and every CB sheet
 * scopes to it; with no estimate selected, the bank pages edit the shared
 * reference libraries instead.
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

import BomExtractionPage from './BomExtraction/BomExtractionPage';
import BomExtractorApiPage from './BomExtractorApiPage';
import BomExtractorApi2Page from './BomExtractorApi2Page';
import MtoLog from './MtoLog';
import CurrencyExchange from '../MaterialManagement/CurrencyExchange';
import PidExtractionPage from './PidExtraction/PidExtractionPage';
import SchedulingEstimatorPage from './SchedulingEstimatorPage';
import ProposalSchedules from '../Schedule/ProposalSchedules';
import EstimateLbsOnePager from '../Schedule/EstimateLbs/EstimateLbsOnePager';
import ScheduleModulesPanel from '../Schedule/ScheduleModulesPanel';
import { ScheduleModel } from '../Schedule';
import EVMReport from '../Reports/EVMReport';
import PartRateDbView from './LookupTables/PartRateDbView';
import RateBenchmarksView from './RateBenchmarks/RateBenchmarksView';
import MechEquipmentHistoryDb from './MechEquipmentHistoryDb';
import UnitRatePage from './UnitRate/UnitRatePage';
import SharePointBookmarksPage from '../Documentation/SharePointBookmarksPage';
import { isProposalScheduleAllowed } from '../../config/proposalScheduleAccess';

// Cost Breakdown v2
import EstimateLogPage from './CostBreakdown/EstimateLogPage';
import CbOnePager from './CostBreakdown/CbOnePager';
import DisciplinesPage from './CostBreakdown/DisciplinesPage';
import DisciplineDetailSheet from './CostBreakdown/DisciplineDetailSheet';
import DisciplineSummarySheet from './CostBreakdown/DisciplineSummarySheet';
import SimpleCbSheet, { SHEETS } from './CostBreakdown/SimpleCbSheet';
import WorkflowHelp from './CostBreakdown/WorkflowHelp';
import ReadMeHelp from './CostBreakdown/ReadMeHelp';
import { BidFormView, ProposalLetterView, ClarificationLetterView } from './CostBreakdown/BidPackageSheets';
import AiAccuracyCurve from './CostBreakdown/AiAccuracyCurve';
import EstimationDashboard from './CostBreakdown/EstimationDashboard';
import { SheetShell, NoEstimateState } from './CostBreakdown/CbUi';
import { getLastMenuForEstimate, setLastMenuForEstimate } from '../../utils/estimationNavMemory';

// Global reference libraries (rates & rentals lookup tables)
import RentalsView from './CostBreakdown/RentalsView';
import LabourRatesView from './CostBreakdown/LabourRatesView';
import LabourRatesBankView from './CostBreakdown/LabourRatesBankView';

// localStorage keys so the active sub-page + selected estimate survive a full
// browser refresh (React Router location.state is wiped on reload).
const LS_MENU_KEY = 'estimation_active_menu';
const LS_ESTIMATE_KEY = 'estimation_selected_estimate';
const readStoredEstimate = () => {
  try { const s = localStorage.getItem(LS_ESTIMATE_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
};
const readStoredMenu = () => {
  try { return localStorage.getItem(LS_MENU_KEY) || null; } catch { return null; }
};

// menu-id fragment -> config discipline key
const MENU_DISCIPLINES = {
  'piping': 'piping',
  'pipe-fab': 'pipe_fab',
  'mechanical': 'mechanical',
  'mr': 'mr',
  'electrical': 'electrical',
  'sm-shop': 'sm_shop',
  'sm-field': 'sm_field',
  'civil': 'civil',
  'structural': 'structural',
  'tank-shop': 'tank_shop',
  'boiler-mkr': 'boiler_maker',
  'hvac': 'hvac',
};

const EstimationPage = ({ onEstimateChange }) => {
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const initialMenuItem = location.state?.menuItem || readStoredMenu() || 'estimate-log';
  const [activeMenuItem, setActiveMenuItem] = useState(initialMenuItem);
  const [selectedEstimate, setSelectedEstimate] = useState(location.state?.estimate || readStoredEstimate());
  const [showWorkflow, setShowWorkflow] = useState(false);
  const [showReadMe, setShowReadMe] = useState(false);

  useEffect(() => {
    if (location.state?.menuItem) {
      setActiveMenuItem(location.state.menuItem);
    }
    if (location.state?.estimate) {
      setSelectedEstimate(location.state.estimate);
      if (onEstimateChange) onEstimateChange(location.state.estimate);
    }
  }, [location.state?.menuItem, location.state?.estimate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link from assignment email: /estimation?estimate=26-LG-0052
  useEffect(() => {
    const hint = new URLSearchParams(location.search).get('estimate');
    if (!hint || !String(hint).trim()) return;
    let cancelled = false;
    const q = encodeURIComponent(String(hint).trim());
    authenticatedApiCall(instance, accounts, 'GET', `/estimates/search?q=${q}&limit=10`)
      .then((res) => {
        const rows = res.data?.data || [];
        const wanted = String(hint).trim().toLowerCase();
        const match = rows.find((e) => String(e.estimate_number || '').toLowerCase() === wanted) || rows[0];
        if (!match?.id || cancelled) return null;
        return authenticatedApiCall(instance, accounts, 'GET', `/estimates/${match.id}`);
      })
      .then((res) => {
        if (!cancelled && res?.data?.estimate) {
          setSelectedEstimate(res.data.estimate);
          setActiveMenuItem(getLastMenuForEstimate(res.data.estimate.id) || 'cost-breakdown-summary');
          if (onEstimateChange) onEstimateChange(res.data.estimate);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist the active sub-page + selected estimate so a refresh restores them.
  // Also remember the sheet per estimate so picking that estimate later
  // returns to where the user left off (not always CB Summary).
  useEffect(() => {
    try { localStorage.setItem(LS_MENU_KEY, activeMenuItem); } catch (e) { /* ignore */ }
    if (selectedEstimate?.id && activeMenuItem) {
      setLastMenuForEstimate(selectedEstimate.id, activeMenuItem);
    }
  }, [activeMenuItem, selectedEstimate?.id]);
  useEffect(() => {
    try {
      if (selectedEstimate) localStorage.setItem(LS_ESTIMATE_KEY, JSON.stringify(selectedEstimate));
      else localStorage.removeItem(LS_ESTIMATE_KEY);
    } catch (e) { /* ignore */ }
  }, [selectedEstimate]);

  // Re-validate the stored estimate against core.estimates on mount. This also
  // migrates anything stale (e.g. an old cb_estimates object) to the real registry.
  useEffect(() => {
    const stored = readStoredEstimate();
    if (!stored?.id) return;
    let cancelled = false;
    authenticatedApiCall(instance, accounts, 'GET', `/estimates/${stored.id}`)
      .then((res) => {
        if (!cancelled && res.data?.estimate) setSelectedEstimate(res.data.estimate);
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedEstimate(null);
          if (onEstimateChange) onEstimateChange(null);
        }
      });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep header estimate label in sync — do NOT clear on unmount (same as department).
  useEffect(() => {
    if (selectedEstimate && onEstimateChange) {
      onEstimateChange(selectedEstimate);
    }
  }, [selectedEstimate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenEstimate = (est) => {
    setSelectedEstimate(est);
    const remembered = getLastMenuForEstimate(est?.id);
    if (remembered) setActiveMenuItem(remembered);
    if (onEstimateChange) onEstimateChange(est || null);
  };

  const isCostBreakdownPage =
    activeMenuItem.startsWith('cost-breakdown') ||
    activeMenuItem === 'sov' ||
    activeMenuItem === 'estimate-log' ||
    activeMenuItem === 'estimation-cost-summary' ||
    activeMenuItem === 'estimation-site-expenses' ||
    activeMenuItem === 'estimation-indirects-bank' ||
    activeMenuItem === 'estimation-crew-calculation' ||
    activeMenuItem === 'estimation-ai-curve' ||
    activeMenuItem === 'estimation-dashboard' ||
    /-(summary|detailed-estimation)$/.test(activeMenuItem);

  const renderContent = () => {
    // Per-discipline menu items: estimation-{discipline}-summary / -detailed-estimation
    const discMatch = activeMenuItem.match(/^estimation-(.+)-(summary|detailed-estimation)$/);
    if (discMatch && MENU_DISCIPLINES[discMatch[1]]) {
      const disciplineKey = MENU_DISCIPLINES[discMatch[1]];
      return discMatch[2] === 'summary'
        ? <DisciplineSummarySheet estimate={selectedEstimate} discipline={disciplineKey} />
        : <DisciplineDetailSheet estimate={selectedEstimate} discipline={disciplineKey} />;
    }

    switch (activeMenuItem) {
      // ---- Cost Breakdown v2 (one estimate spine) ----
      case 'estimate-log':
        return <EstimateLogPage onOpenEstimate={handleOpenEstimate} selectedEstimate={selectedEstimate} />;
      case 'estimate-sharepoint':
        return selectedEstimate
          ? (
            <SharePointBookmarksPage
              estimateId={selectedEstimate.id}
              rootUrl={selectedEstimate.sharepoint_url || null}
              rootLabel="Open estimate SharePoint folder"
            />
          )
          : (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🔗</div>
              <h2>No Estimate Selected</h2>
              <p>Pick an estimate from the header dropdown to bookmark its SharePoint folders.</p>
            </div>
          );
      case 'estimate-autodesk-build':
        return selectedEstimate
          ? (
            <SharePointBookmarksPage
              estimateId={selectedEstimate.id}
              provider="autodesk_build"
              rootUrl={selectedEstimate.autodesk_build_url || null}
              rootLabel="Open Autodesk Build project"
            />
          )
          : (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🏗️</div>
              <h2>No Estimate Selected</h2>
              <p>Pick an estimate from the header dropdown to link Autodesk Build.</p>
            </div>
          );
      case 'estimate-procore':
        return selectedEstimate
          ? (
            <SharePointBookmarksPage
              estimateId={selectedEstimate.id}
              provider="procore"
              rootUrl={selectedEstimate.procore_url || null}
              rootLabel="Open Procore project"
            />
          )
          : (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🚧</div>
              <h2>No Estimate Selected</h2>
              <p>Pick an estimate from the header dropdown to link Procore.</p>
            </div>
          );
      case 'cost-breakdown-summary':
      case 'estimation-cost-summary':
        return <CbOnePager estimate={selectedEstimate} onOpenDiscipline={() => setActiveMenuItem('cost-breakdown-disciplines')} />;
      case 'estimation-dashboard':
        return (
          <EstimationDashboard
            estimate={selectedEstimate}
            onNavigate={(menuId) => setActiveMenuItem(menuId)}
          />
        );
      case 'estimation-ai-curve':
        if (!selectedEstimate?.id) return <NoEstimateState section="AI Accuracy Curve" />;
        return (
          <SheetShell
            title="📈 AI Accuracy Curve"
            subtitle={`Estimate ${selectedEstimate.estimate_number} · day-0 seed vs human package over time`}
            gradient="linear-gradient(135deg, #0f766e, #14b8a6)"
          >
            <AiAccuracyCurve estimate={selectedEstimate} />
          </SheetShell>
        );
      case 'cost-breakdown-disciplines':
        return <DisciplinesPage estimate={selectedEstimate} />;
      case 'cost-breakdown-indirects':
      case 'estimation-indirects-bank':
        return <SimpleCbSheet estimate={activeMenuItem === 'estimation-indirects-bank' ? null : selectedEstimate} sheet={SHEETS.indirects} />;
      case 'cost-breakdown-site-expenses':
      case 'estimation-site-expenses':
        return <SimpleCbSheet estimate={activeMenuItem === 'estimation-site-expenses' ? null : selectedEstimate} sheet={SHEETS.siteExpenses} />;
      case 'estimation-crew-calculation':
        return <SimpleCbSheet estimate={selectedEstimate} sheet={SHEETS.crew} />;
      case 'cost-breakdown-clarifications':
        return <SimpleCbSheet estimate={selectedEstimate} sheet={SHEETS.clarifications} />;
      case 'sov':
        return <SimpleCbSheet estimate={selectedEstimate} sheet={SHEETS.sov} />;

      // ---- Global reference libraries ----
      case 'estimation-labour-rates-bank':
        return <LabourRatesBankView />;
      case 'cost-breakdown-rentals':
        return <RentalsView estimate={selectedEstimate} />;
      case 'cost-breakdown-labour-rates':
        return <LabourRatesView />;

      // ---- Other estimation tools (unchanged) ----
      case 'estimation-piping-bom-extraction':
        return <BomExtractionPage jobNumber={null} />;
      case 'estimation-piping-bom-extractor-api':
        return <BomExtractorApiPage />;
      case 'estimation-piping-bom-extractor-api-2':
        return <BomExtractorApi2Page jobNumber={null} />;
      case 'estimation-piping-pid-extraction':
        return <PidExtractionPage />;
      case 'mto':
        return selectedEstimate
          ? <MtoLog jobNumber={null} estimateNumber={selectedEstimate.estimate_number} />
          : (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>📋</div>
              <h2>No Estimate Selected</h2>
              <p>Pick an estimate from the header dropdown or the <strong>Estimate Log</strong> to open its MTO.</p>
            </div>
          );
      case 'currency-exchange':
        return <CurrencyExchange />;
      case 'estimation-scheduling':
        return <SchedulingEstimatorPage />;
      case 'estimation-proposal-schedule':
        if (!isProposalScheduleAllowed(accounts[0]?.username)) {
          return (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🔒</div>
              <h2>Access restricted</h2>
              <p>Proposal Schedule is limited to authorized Project Controls users.</p>
            </div>
          );
        }
        return <ProposalSchedules estimate={selectedEstimate} />;
      case 'estimation-evm-report':
        if (!selectedEstimate?.estimate_number) {
          return <NoEstimateState section="EVM Report" />;
        }
        return (
          <EVMReport
            jobNumber={selectedEstimate.job_number || null}
            estimateNumber={selectedEstimate.estimate_number}
          />
        );
      case 'estimation-schedule-modules':
        if (!selectedEstimate?.estimate_number) {
          return <NoEstimateState section="Schedule options" />;
        }
        return (
          <ScheduleModulesPanel
            scopeType="estimate"
            scopeKey={selectedEstimate.estimate_number}
            title="Schedule options"
          />
        );
      case 'estimation-lbs':
        if (!selectedEstimate?.estimate_number) {
          return <NoEstimateState section="Location-Based Schedule" />;
        }
        return <EstimateLbsOnePager estimate={selectedEstimate} />;
      case 'estimation-schedule-model':
        if (!selectedEstimate?.estimate_number) {
          return <NoEstimateState section="Model" />;
        }
        return (
          <ScheduleModel
            jobNumber={selectedEstimate.job_number || null}
            estimateNumber={selectedEstimate.estimate_number}
          />
        );
      case 'estimation-piping-lt-part-rate-db':
      case 'estimation-part-rate-db':
        return <PartRateDbView />;
      case 'estimation-rate-benchmarks':
        return <RateBenchmarksView />;
      case 'estimation-mechanical-equipment-history':
        return <MechEquipmentHistoryDb />;
      case 'estimation-unit-rate':
        return <UnitRatePage />;

      case 'cost-breakdown-bid-form':
        return <BidFormView estimate={selectedEstimate} />;
      case 'cost-breakdown-proposal-letter':
        return <ProposalLetterView estimate={selectedEstimate} />;
      case 'cost-breakdown-llm-clarification-letter':
        return <ClarificationLetterView estimate={selectedEstimate} />;

      // ---- Not built yet ----
      case 'material-index':
      case 'estimation-piping-convertors':
      case 'shop-field-ratio':
        return (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🚧</div>
            <h2 style={{ marginBottom: '8px' }}>Coming Soon</h2>
            <p>This feature is under development.</p>
          </div>
        );

      default:
        if (activeMenuItem?.startsWith('estimation-piping-lt-')) {
          return (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6c757d' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🚧</div>
              <h2>Lookup Table — Coming Soon</h2>
            </div>
          );
        }
        return (
          <div style={{ padding: '24px', textAlign: 'center', color: '#6c757d' }}>
            <h2>Select an estimation tool</h2>
            <p>Choose from the menu on the left to get started.</p>
          </div>
        );
    }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Cost Breakdown toolbar: active estimate + Workflow/Help */}
      {isCostBreakdownPage && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0.5rem 1rem', backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {selectedEstimate ? (
              <>
                <span style={{ fontSize: '0.85rem', color: '#495057' }}>
                  Active Estimate: <strong style={{ color: '#dc3545' }}>{selectedEstimate.estimate_number}</strong>
                  {selectedEstimate.estimate_name ? ` — ${selectedEstimate.estimate_name}` : ''}
                </span>
                <button
                  onClick={() => {
                    setSelectedEstimate(null);
                    if (onEstimateChange) onEstimateChange(null);
                  }}
                  title="Deselect estimate (bank pages then edit the shared libraries)"
                  style={{
                    padding: '2px 8px', backgroundColor: 'white', color: '#6c757d',
                    border: '1px solid #dee2e6', borderRadius: '10px', cursor: 'pointer', fontSize: '0.72rem',
                  }}
                >✕ clear</button>
              </>
            ) : (
              <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                No estimate selected — bank pages edit the shared <strong>libraries</strong>
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => setShowWorkflow(true)} style={{
              padding: '5px 12px', backgroundColor: 'white', color: '#495057',
              border: '1px solid #dee2e6', borderRadius: '4px', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: '500',
            }}>📋 Workflow</button>
            <button onClick={() => setShowReadMe(true)} style={{
              padding: '5px 12px', backgroundColor: 'white', color: '#495057',
              border: '1px solid #dee2e6', borderRadius: '4px', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: '500',
            }}>❓ Help</button>
          </div>
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {renderContent()}
      </div>

      <WorkflowHelp isOpen={showWorkflow} onClose={() => setShowWorkflow(false)} />
      <ReadMeHelp isOpen={showReadMe} onClose={() => setShowReadMe(false)} />
    </div>
  );
};

export default EstimationPage;
